// =====================================================================
// Permissões de acesso por categoria — mesmas regras do legado.
// Usado pelo Shell (filtra o menu) e pelo PageGuard (bloqueia URL direta).
// =====================================================================

// Páginas de cada categoria. Espelha as CATEGORIES do Shell + rotas do App.
export const PAGINAS_POR_CATEGORIA: Record<string, string[]> = {
  atendimento: [
    'atendimento-painel', 'pessoas', 'conversas', 'atendimento-comunidade', 'respostas-formulario',
    'alunos-gestao', 'alunos-atrasados', 'alunos-a-vencer', 'alunos-sem-estudar', 'aprovados', 'processo',
  ],
  'planos-estudo': [
    'admin-metas', 'admin-config-plano', 'admin-alunos-config', 'admin-aulas-apoio',
    'admin-avisos-plano', 'admin-simulados-cronograma', 'admin-cadernos', 'admin-cronograma-planos',
  ],
  pedagogico: [
    'erros-reportados', 'admin-simulados', 'cadernos-questoes',
    'encontros', 'admin-flashcards',
    'avaliacoes-conteudo',
    // Banco de Questões (admin): pedagógico; o BFF /api/banco-questoes também exige pedagogico/admin
    'banco-questoes-painel', 'banco-questoes-lista', 'banco-questoes-revisao',
    'banco-questoes-recursos', 'banco-questoes-comentarios', 'banco-questoes-acessos', 'banco-questoes-por-meta', 'banco-questoes-ipq',
  ],
  discursivas: [
    'admin-discursivas-correcao', 'admin-discursivas', 'lista-discursivas',
  ],
  comercial: [
    'leads-esteira',
    'comercial-dashboard', 'comercial-vendas', 'comercial-relatorio', 'comercial-config',
    'negociacao', 'embaixadores',
  ],
  admin: [
    'admin-usuarios', 'admin-perfis',
    'admin-logs', 'admin-corretores',
    'migracao-tutory', 'whatsapp-gerenciador', 'whatsapp-config', 'whatsapp-disparos',
  ],
  // Bucket dedicado (não some dentro de 'atendimento'/'pedagogico'): a página
  // aparece no menu dos dois, e o acesso é liberado pras duas categorias — ver
  // o caso especial em categoriaPermitida(), mesmo padrão de 'discursivas'.
  'corrigir-email': ['corrigir-email'],
  // Migração Tutory: mesmo padrão — menu de Atendimento, Pedagógico e Administração (29/10/2026).
  'migracao-tutory': ['migracao-tutory'],
  // Fila de Problemas do Portal: aberta a atendimento/pedagogico/comercial/admin
  // (qualquer setor reporta e acompanha) — ver categoriaPermitida().
  'problemas-portal': ['problemas-portal'],
  // Conteúdo › Social Seller (cadastro do Direct) e Debriefing (painel do Instagram) — comercial, marketing e admin (ver categoriaPermitida).
  conteudo: ['conteudo-calendario', 'conteudo-especialistas', 'conteudo-direct', 'conteudo-prompt-social-seller', 'conteudo-social-seller', 'conteudo-debriefing'],
  // Marketing (Calendário de marketing, Funis, Gestão de e-mails, Tráfego) — comercial, marketing e admin.
  marketing: ['marketing-atividades', 'calendario-lancamentos', 'marketing-funis', 'marketing-grupos', 'marketing-email', 'marketing-trafego'],
  // Bônus de venda (docs/bonus): aparece no menu de atendimento E comercial —
  // o comercial decide os bônus na negociação, o atendimento entrega.
  // As escritas restritas seguem protegidas pelo authorize do BFF.
  bonus: ['bonus'],
  // Central WhatsApp (docs/atendimento-whatsapp): atendimento + pedagógico + admin (decisão A3).
  'central-whatsapp': ['central-whatsapp', 'whatsapp-simulador'],
  // Base do agente: revisão é da equipe pedagógica (F3c).
  'whatsapp-conhecimento': ['whatsapp-conhecimento'],
};

// Páginas utilitárias sempre acessíveis a quem está logado.
// 'homologacao' é a Central de Homologação (TEMPORÁRIA): liberada a todos os perfis.
export const PAGINAS_LIVRES = new Set<string>([
  '', 'home', 'calendario-acoes', 'calendario-lancamentos', 'formulario-visualizar',
  'homologacao', 'links', 'atividades',
  // Assistente (IA): a página é de todos; os setores que cada um vê são decididos pelo BFF (/api/assistente/setores)
  'assistente',
]);

// Páginas de Discursivas que usam o BFF /api/discursivas (authorize pedagogico/admin):
// temas e lista completa. O corretor (e o atendimento) só usa a Lista de correção.
const PAGINAS_DISCURSIVAS_GESTAO = new Set<string>(['admin-discursivas', 'lista-discursivas']);

// Índice reverso: página → categoria.
const PAGINA_PARA_CATEGORIA: Record<string, string> = {};
for (const [cat, pages] of Object.entries(PAGINAS_POR_CATEGORIA)) {
  for (const p of pages) PAGINA_PARA_CATEGORIA[p] = cat;
}

// ── Abas que cada perfil vê (escolhidas no "Editar" de Perfis) ───────────────
// O perfil guarda as abas marcadas em `categorias` (ids abaixo = ids das CATEGORIES do Shell) + a marca
// ABAS_ESCOLHIDAS. Perfil sem a marca (ainda não salvo no formato novo) segue as regras antigas abaixo.
// atendimento/pedagogico/comercial/admin também são as categorias que o BFF confere (authorize).
export const ABAS_ESCOLHIDAS = '_abas';
export const ABAS_MENU: { id: string; label: string }[] = [
  { id: 'atendimento', label: 'Atendimento ao Aluno' },
  { id: 'planos-estudo', label: 'Planos de Estudo' },
  { id: 'pedagogico', label: 'Pedagógico' },
  { id: 'discursivas', label: 'Discursivas' },
  { id: 'comercial', label: 'Comercial' },
  { id: 'central-ordens', label: 'Gestão da equipe' },
  { id: 'financeiro', label: 'Financeiro' },
  { id: 'admin', label: 'Administração' },
];
// Permissões que não são aba do menu (continuam no perfil).
export const OUTRAS_PERMISSOES: { key: string; label: string }[] = [
  { key: 'corretor', label: 'Corretor de discursivas' },
  { key: 'marketing', label: 'Marketing' },
];

/** Abas que o perfil vê hoje (para marcar no "Editar"). */
export const abasDoPerfil = (categorias: string[]) => ABAS_MENU.filter((a) => categoriaPermitida(a.id, categorias)).map((a) => a.id);

// Uma categoria do menu é visível para o usuário? (regras do legado)
//   - corretor exclusivo (só 'corretor') → vê apenas Discursivas
//   - Discursivas → liberada para atendimento/pedagogico/admin/corretor
//   - demais → visível se o usuário tem a categoria
export function categoriaPermitida(catId: string, categorias: string[]): boolean {
  // Homologação (TEMPORÁRIA): visível a qualquer perfil logado, inclusive corretor.
  if (catId === 'homologacao') return true;
  // Links da equipe: qualquer perfil logado.
  if (catId === 'links') return true;
  // Conteúdo (Social Seller): comercial, marketing e admin.
  if (catId === 'conteudo') return ['comercial', 'marketing', 'admin'].some((c) => categorias.includes(c));
  // Marketing (Lançamentos…): mesmas permissões de Conteúdo.
  if (catId === 'marketing') return ['comercial', 'marketing', 'admin'].some((c) => categorias.includes(c));
  // Perfil salvo no formato novo: vê exatamente as abas marcadas (inclusive quem tem Administração).
  if (categorias.includes(ABAS_ESCOLHIDAS) && ABAS_MENU.some((a) => a.id === catId)) return categorias.includes(catId);
  // Formato antigo: admin vê todas as abas.
  if (categorias.includes('admin') && ABAS_MENU.some((a) => a.id === catId)) return true;
  // Financeiro: só administradores (regra antiga)
  if (catId === 'financeiro') return categorias.includes('admin');
  const ehCorretorExclusivo = categorias.length === 1 && categorias[0] === 'corretor';
  if (ehCorretorExclusivo) return catId === 'discursivas';
  // Calendário (geral + marketing): toda a equipe.
  if (catId === 'calendario') return true;
  // Central de Ordens: toda a equipe (a própria Central controla quem cria, executa e revisa).
  if (catId === 'central-ordens') return true;
  // Início (painel, calendário geral, gestão de atividades, gestão de pessoas, links): toda a equipe.
  if (catId === 'inicio') return true;
  if (catId === 'discursivas') {
    return ['atendimento', 'pedagogico', 'admin', 'corretor'].some((c) => categorias.includes(c));
  }
  if (catId === 'migracao-tutory') {
    return ['atendimento', 'pedagogico', 'admin'].some((c) => categorias.includes(c));
  }
  if (catId === 'corrigir-email') {
    return ['atendimento', 'pedagogico', 'admin'].some((c) => categorias.includes(c));
  }
  if (catId === 'whatsapp') {
    return ['atendimento', 'pedagogico', 'admin'].some((c) => categorias.includes(c));
  }
  if (catId === 'whatsapp-conhecimento') {
    return ['pedagogico', 'admin'].some((c) => categorias.includes(c));
  }
  if (catId === 'central-whatsapp') {
    return ['atendimento', 'pedagogico', 'admin'].some((c) => categorias.includes(c));
  }
  if (catId === 'bonus') {
    return ['atendimento', 'comercial', 'admin'].some((c) => categorias.includes(c));
  }
  if (catId === 'problemas-portal') {
    return ['atendimento', 'pedagogico', 'comercial', 'admin'].some((c) => categorias.includes(c));
  }
  return categorias.includes(catId);
}

// O usuário tem acesso a uma página (rota)?
export function temAcessoPagina(page: string, categorias: string[]): boolean {
  const p = (page || '').replace(/^\//, '');
  // Gestão da equipe e Financeiro vivem em /ordens/* (a Central confere de novo quem pode o quê)
  if (/^ordens\/financeiro(\/|$)/.test(p)) return categoriaPermitida('financeiro', categorias);
  if (p === 'ordens' || p.startsWith('ordens/')) return categoriaPermitida('central-ordens', categorias);
  if (PAGINAS_LIVRES.has(p)) return true;
  if (PAGINAS_DISCURSIVAS_GESTAO.has(p)) {
    return ['pedagogico', 'admin'].some((c) => categorias.includes(c));
  }
  const cat = PAGINA_PARA_CATEGORIA[p];
  if (!cat) return true; // página não mapeada → não bloqueia (utilitária)
  return categoriaPermitida(cat, categorias);
}
